import React, { useEffect, useState } from 'react';
import {
  IonContent,
  IonRefresher,
  IonRefresherContent,
  IonModal,
  IonPage,
  IonToolbar,
  IonHeader,
  IonButtons,
} from '@ionic/react';
import { ApiService } from '../api/ApiService';
import { RefresherEventDetail } from '@ionic/core';
import { UserState, userStore } from '../api/AuthService';
import { LoadInfo as StoredLoadInfo } from '../api/model/LoadInfo';
import { useHistory } from 'react-router-dom';
import { toastStore } from '../components/AppToast';
import { refreshOutline } from 'ionicons/icons';
import CurrentLoadFirstIn from '../components/walkthrough/individualPage/CurrentLoadFirstIn';
import LoadDetailHeader, { useTransparentHeaderContrast } from '../components/LoadDetailHeader';
import { useLogPageVisit } from '../utils/logPageVisit';
import commentIcon from '../imgs/quotes.png';

interface LoadInfo {
  approved: string;
  blNumber: string;
  comments: string;
  consigneeAddr1: string;
  consigneeAddr2: string;
  consigneeCity: string;
  consigneeCode: string;
  consigneeName: string;
  consigneeNameOrExt: string;
  consigneePhone: string;
  consigneeState: string;
  consigneeZip: string;
  deliveryEndTime: string;
  deliveryStartTime: string;
  pickupNumber: string;
  poNumber: string;
  deliveryNumber: string;
  dispatchNumber: string;
  driver1: string;
  driver2: string;
  driverLoad: boolean | string;
  driverUnload: boolean | string;
  loadNumber: string;
  milesEmpty: number;
  milesLoaded: number;
  milesTotal: number;
  numberOfStops: string;
  orderNumber: string;
  pickupEndTime: string;
  pickupStartTime: string;
  preloadTrailer: string;
  rejectReason: string;
  shipperAddr1: string;
  shipperAddr2: string;
  shipperCity: string;
  shipperCode: string;
  shipperName: string;
  shipperPhone: string;
  shipperState: string;
  shipperZip: string;
  trailer: string;
  tarpPay: boolean;
  tcall: number;
  orderStatus: string;
}

function placeLine(city?: string, state?: string): string {
  return [city, state].filter(Boolean).join(', ') || '—';
}

function formatMiles(value?: number | null): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString();
}

const CurrentLoad: React.FC = () => {
  useLogPageVisit('LP Checked Load', 'User checked their current load');
  const history = useHistory();
  const { storedLoadInfo } = userStore((state: UserState) => ({
    storedLoadInfo: state.loadInfo,
  }));

  const [loadInfo, setLoadInfo] = useState<LoadInfo>();
  const [stopInfo, setStopInfo] = useState<any[]>([]);
  const [hasStops, setHasStops] = useState(false);
  const [hasTarpPay, setHasTarpPay] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [videoLink, setVideoLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { onSheet, onIonScroll } = useTransparentHeaderContrast();

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setTimeout(() => setMounted(true), 30);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  function processLoadInfo(load?: StoredLoadInfo) {
    if (!load) {
      toastStore.getState().setToast('No load found.');
      history.push('/');
      return;
    }

    const ld = JSON.parse(JSON.stringify(load));
    formatTime(ld.pickupStartTime, ld.deliveryStartTime);

    ld.comments = (load.comments || []).join('\n');
    switch (ld.driverLoad) {
      case 'N':
        ld.driverLoad = 'Live Load';
        break;
      case 'Y':
        ld.driverLoad = 'Y';
        break;
      case 'W':
        ld.driverLoad = 'Pre loaded - pickup by end of window';
        break;
      case 'P':
        ld.driverLoad = 'Pre loaded';
        break;
      default:
        break;
    }
    switch (ld.driverUnload !== null ? ld.driverUnload : ld.driverUnloadAlt) {
      case 'N':
        ld.driverUnload = 'Live Unload';
        break;
      case 'Y':
        ld.driverUnload = 'Y';
        break;
      case 'W':
        ld.driverUnload = 'Drop off - deliver by end of window';
        break;
      case 'D':
        ld.driverUnload = 'Drop off';
        break;
      default:
        break;
    }
    setLoadInfo(ld);
    setLoading(false);
    if (ld.tarpPay) {
      checkTarpPhotos(ld.orderNumber);
      setHasTarpPay(true);
    }
  }

  function processStopInfo(stops: any) {
    if (!stops || stops.length < 3) return;
    const tempArray: any[] = [];
    stops.forEach((element: any) => {
      const tempElement: any = element;
      if (tempElement.sequenceNumber !== 1 && tempElement.sequenceNumber !== 90) {
        tempArray.push(element);
      }
    });
    setHasStops(true);
    setStopInfo(tempArray);
  }

  function checkTarpPhotos(orderNum: number) {
    ApiService.getPhotoList('currentLoad', { load: orderNum, type: 'tarp' }).then(it => {
      void it?.photos?.length;
    });
  }

  const formatTime = (pickup: string, dropoff: string) => {
    if (pickup && dropoff) {
      const now = new Date().getTime();
      const pickupTime = new Date(pickup).getTime();
      const pickupDiff = pickupTime - now;
      const dropoffTime = new Date(dropoff).getTime();
      const dropoffDiff = dropoffTime - now;
      if (pickupDiff > 0) {
        const minutesTotal = Math.floor(pickupDiff / 60000);
        const days = Math.floor(minutesTotal / 24 / 60);
        const hours = Math.floor(minutesTotal / 60);
        const minutesLeft = minutesTotal % 60;
        return { days, hours, min: minutesLeft };
      } else if (dropoffDiff > 0) {
        const minutesTotal = Math.floor(dropoffDiff / 60000);
        const days = Math.floor(minutesTotal / 24 / 60);
        const hours = Math.floor(minutesTotal / 60);
        const minutesLeft = minutesTotal % 60;
        return { days, hours, min: minutesLeft };
      }
    }
  };

  useEffect(() => {
    processLoadInfo(storedLoadInfo);
    processStopInfo(userStore.getState().stopDetails);
    checkForVideo(storedLoadInfo);
  }, []);

  const formatComment = (comment: string) => {
    if (comment) return comment;
    return 'No Comment';
  };

  function doRefresh(event: CustomEvent<RefresherEventDetail>) {
    window.location.replace('./currentload');
    event.detail.complete();
  }

  function checkForVideo(info: any) {
    if (!info?.shipperCode) return;
    ApiService.getVideosList('safety', { type: 'safety' }).then(it => setVideo(it, info.shipperCode));
  }

  const setVideo = (videoList: any, shipperCode: string) => {
    videoList.forEach((element: any) => {
      if (shipperCode === 'QOMT' && element.id === 57) {
        setVideoLink(element.youtube);
      } else if (shipperCode === 'MTOAK' && element.id === 59) {
        setVideoLink(element.youtube);
      } else if (shipperCode === 'DSDWH' && element.id === 60) {
        setVideoLink(element.youtube);
      }
    });
  };

  const showTrailerWarning =
    !(parseInt(loadInfo?.dispatchNumber || '0') >= 1 && loadInfo?.orderStatus === 'D');
  const showFacilityVideo =
    loadInfo?.shipperCode === 'QOMT' ||
    loadInfo?.shipperCode === 'MTOAK' ||
    loadInfo?.shipperCode === 'DSDWH';
  const origin = placeLine(loadInfo?.shipperCity, loadInfo?.shipperState);
  const destination = placeLine(loadInfo?.consigneeCity, loadInfo?.consigneeState);
  const loadUnload =
    loadInfo?.driverLoad || loadInfo?.driverUnload
      ? `${loadInfo?.driverLoad || '—'} / ${loadInfo?.driverUnload || '—'}`
      : '—';

  const goBack = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.replace('/Home');
    }
  };

  return (
    <IonPage>
      <LoadDetailHeader title="Current Load" onBack={goBack} onSheet={onSheet} />
      <IonContent
        fullscreen
        className="design-a-content"
        scrollEvents={true}
        onIonScroll={onIonScroll}
      >
        <CurrentLoadFirstIn>
          <IonModal isOpen={loading} id="transparent">
            <div className="modal-div">
              <div id="modal">
                <div className="loading-div">
                  <div className="snippet" data-title=".dot-flashing" style={{ margin: '4px 16px' }}>
                    <div className="stage">
                      <div className="dot-flashing" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </IonModal>
          <IonModal isOpen={showVideo} id="transparent">
            <IonHeader>
              <IonToolbar color="primary">
                <IonButtons slot="start">
                  <button
                    type="button"
                    onClick={() => setShowVideo(false)}
                    style={{ background: 'transparent' }}
                  >
                    <h6>Back</h6>
                  </button>
                </IonButtons>
              </IonToolbar>
            </IonHeader>
            <IonContent>
              <iframe
                id="video-modal"
                title="load video"
                width="100%"
                height="40%"
                src={'https://www.youtube.com/embed/' + videoLink}
              />
            </IonContent>
          </IonModal>

          <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
            <IonRefresherContent
              pullingIcon={refreshOutline}
              pullingText="Pull to refresh"
              refreshingSpinner="circles"
            />
          </IonRefresher>

          <div className={`design-a design-a--detail${mounted ? ' design-a--mounted' : ''}`}>
            <div className="home-hero load-detail-hero" id="CurrentLoadTop">
              <div className="load-detail-hero__eyebrow">Load #{loadInfo?.loadNumber || '—'}</div>
              <div className="load-detail-hero__route">
                <span className="load-detail-hero__origin">{origin}</span>
                <span className="load-detail-hero__arrow" aria-hidden="true">
                  →
                </span>
                <span className="load-detail-hero__destination home-display">{destination}</span>
              </div>
              <div className="load-distance load-detail-hero__distance">
                <div className="load-distance__label">Load details</div>
                <div className="load-distance__grid">
                  <div className="load-distance__row">
                    <div className="load-distance__col load-distance__col--first">
                      <div className="load-distance__value">{formatMiles(loadInfo?.milesTotal)}</div>
                      <div className="load-distance__unit">total miles</div>
                    </div>
                    <div className="load-distance__col">
                      <div className="load-distance__value">{formatMiles(loadInfo?.milesEmpty)}</div>
                      <div className="load-distance__unit">empty miles</div>
                    </div>
                  </div>
                  <div className="load-distance__col load-distance__col--eta">
                    <div className="load-distance__value">{formatMiles(loadInfo?.milesLoaded)}</div>
                    <div className="load-distance__unit">loaded miles</div>
                  </div>
                  <div className="load-distance__col load-distance__col--eta">
                    <div className="load-distance__value load-detail-hero__distance-value--sm">{loadUnload}</div>
                    <div className="load-distance__unit">pickup &amp; delivery</div>
                  </div>
                </div>
              </div>
              {(showTrailerWarning || loadInfo?.tcall === 1 || showFacilityVideo) && (
                <div className="load-detail-hero__alerts">
                  {showTrailerWarning ? (
                    <div className="load-detail-hero__alert load-detail-hero__alert--warn">
                      Bring a trailer to shipper
                    </div>
                  ) : null}
                  {loadInfo?.tcall === 1 ? (
                    <div className="load-detail-hero__alert">TCall: Yes</div>
                  ) : null}
                  {showFacilityVideo ? (
                    <button
                      type="button"
                      className="load-detail-hero__alert load-detail-hero__alert--link"
                      onClick={() => setShowVideo(true)}
                    >
                      Watch facility video
                    </button>
                  ) : null}
                </div>
              )}
            </div>

            <div className="home-sheet load-detail-sheet">
              <div className="load-detail-card load-detail-card--meta" id="CurrentLoadMeta">
                <div className="load-detail-meta">
                  <div className="load-detail-meta__row">
                    <span className="load-detail-meta__label">Trailer</span>
                    <span className="load-detail-meta__value">{loadInfo?.trailer || '—'}</span>
                  </div>
                  <div className="load-detail-meta__row">
                    <span className="load-detail-meta__label">Dispatch</span>
                    <span className="load-detail-meta__value">{loadInfo?.dispatchNumber || '—'}</span>
                  </div>
                  <div className="load-detail-meta__row">
                    <span className="load-detail-meta__label">Status</span>
                    <span className="load-detail-meta__value">{loadInfo?.orderStatus || '—'}</span>
                  </div>
                  <div className="load-detail-meta__row">
                    <span className="load-detail-meta__label">Stops</span>
                    <span className="load-detail-meta__value">{loadInfo?.numberOfStops || '—'}</span>
                  </div>
                  {loadInfo?.preloadTrailer ? (
                    <div className="load-detail-meta__row">
                      <span className="load-detail-meta__label">Preloaded</span>
                      <span className="load-detail-meta__value">{loadInfo.preloadTrailer}</span>
                    </div>
                  ) : null}
                  <div className="load-detail-meta__row">
                    <span className="load-detail-meta__label">Tarp photos</span>
                    <span className="load-detail-meta__value">
                      {hasTarpPay ? loadInfo?.approved : 'Not Required'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="load-detail-card" id="CurrentLoadLoadAt">
                <div className="load-detail-card__header">
                  <div className="load-detail-badge load-detail-badge--load">L</div>
                  <div className="load-detail-card__title">Load At</div>
                </div>
                <div className="load-detail-card__window">
                  {loadInfo?.pickupStartTime} – {loadInfo?.pickupEndTime}
                </div>
                <div className="load-detail-card__name home-display">{loadInfo?.shipperName || '—'}</div>
                <div className="load-detail-card__address">
                  {loadInfo?.shipperAddr1 ? <div>{loadInfo.shipperAddr1}</div> : null}
                  {loadInfo?.shipperAddr2 ? <div>{loadInfo.shipperAddr2}</div> : null}
                  <div>
                    {loadInfo?.shipperCity}, {loadInfo?.shipperState} {loadInfo?.shipperZip}
                  </div>
                  {loadInfo?.shipperPhone ? <div>{loadInfo.shipperPhone}</div> : null}
                </div>
                <div className="load-detail-grid">
                  <div className="load-detail-grid__cell">
                    <span className="load-detail-grid__label">PO</span>
                    <span className="load-detail-grid__value">{loadInfo?.poNumber || '—'}</span>
                  </div>
                  <div className="load-detail-grid__cell">
                    <span className="load-detail-grid__label">Pickup</span>
                    <span className="load-detail-grid__value">{loadInfo?.pickupNumber || '—'}</span>
                  </div>
                  <div className="load-detail-grid__cell">
                    <span className="load-detail-grid__label">Order</span>
                    <span className="load-detail-grid__value">{loadInfo?.orderNumber || '—'}</span>
                  </div>
                  <div className="load-detail-grid__cell">
                    <span className="load-detail-grid__label">BOL</span>
                    <span className="load-detail-grid__value">{loadInfo?.blNumber || '—'}</span>
                  </div>
                </div>
              </div>

              {hasStops
                ? stopInfo.map((element: any, index: number) => (
                    <div className="load-detail-card" key={`stop-${element.sequenceNumber || index}`}>
                      <div className="load-detail-card__header">
                        <div className="load-detail-badge load-detail-badge--stop">S</div>
                        <div className="load-detail-card__title">Stop At</div>
                      </div>
                      <div className="load-detail-card__name home-display">
                        {element?.customerName || '—'}
                      </div>
                      <div className="load-detail-card__address">
                        {element?.address1 ? <div>{element.address1}</div> : null}
                        {element?.address2 ? <div>{element.address2}</div> : null}
                        <div>
                          {element?.city}, {element?.state} {element?.zipCode}
                        </div>
                      </div>
                    </div>
                  ))
                : null}

              <div className="load-detail-card" id="CurrentLoadCosignee">
                <div className="load-detail-card__header">
                  <div className="load-detail-badge load-detail-badge--consignee">C</div>
                  <div className="load-detail-card__title">Consignee</div>
                </div>
                <div className="load-detail-card__window">
                  {loadInfo?.deliveryStartTime} – {loadInfo?.deliveryEndTime}
                </div>
                <div className="load-detail-card__name home-display">
                  {loadInfo?.consigneeName || '—'} {loadInfo?.consigneeNameOrExt || ''}
                </div>
                <div className="load-detail-card__address">
                  {loadInfo?.consigneeAddr1 ? <div>{loadInfo.consigneeAddr1}</div> : null}
                  {loadInfo?.consigneeAddr2 ? <div>{loadInfo.consigneeAddr2}</div> : null}
                  <div>
                    {loadInfo?.consigneeCity}, {loadInfo?.consigneeState} {loadInfo?.consigneeZip}
                  </div>
                  {loadInfo?.consigneePhone ? <div>{loadInfo.consigneePhone}</div> : null}
                </div>
                <div className="load-detail-grid">
                  <div className="load-detail-grid__cell load-detail-grid__cell--span2">
                    <span className="load-detail-grid__label">Delivery</span>
                    <span className="load-detail-grid__value">{loadInfo?.deliveryNumber || '—'}</span>
                  </div>
                </div>
              </div>

              <div className="load-detail-card" id="CurrentLoadComments">
                <div className="load-detail-card__header">
                  <div className="load-detail-badge load-detail-badge--comments">
                    <img src={commentIcon} alt="" className="load-detail-badge__img" />
                  </div>
                  <div className="load-detail-card__title">Comments</div>
                </div>
                <p className="load-detail-comments">{formatComment(loadInfo?.comments ?? '')}</p>
              </div>
            </div>
          </div>
        </CurrentLoadFirstIn>
      </IonContent>
    </IonPage>
  );
};

export default CurrentLoad;
